'use client';

import logoGif from '@/assets/images/common/logo.gif';
import ColorsFamilyFilter from '@/components/filter-section/colors-family-filter';
import ProductSort from '@/components/ProductSort';
import { ACTIVATE_ATTIRE_CATEGORIES } from '@/constants/activate-attire-categories';
import { allSortMethod } from '@/constants/all-sort-method';
import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { NEXT_IMAGE_PLACEHOLDER } from '@/constants/next-image-placeholder';
import { SEO_CATEGORY_MAP } from '@/constants/seoCategoryMap';
import { websiteEnv } from '@/constants/website-env';
import { useAppContext } from '@/context/app';
import useIsMobile from '@/hooks/use-is-mobile';
import {
  GET_CATEGORY_FILTER_OPTIONS,
  GET_CATEGORY_FILTER_OPTIONS_BY_FILTER,
  GET_CATEGORY_PRODUCTS,
  GET_CATEGORY_PRODUCTS_BY_FILTER,
  GET_CATEGORY_QUERY_SKU,
} from '@/typed-graphql/query';
import {
  convertStateToNewFilter,
  getFilterStateFromPath,
  resolveCategoryLayerFilterLabels,
} from '@/util/category-filter-helper';
import { gql, useQuery } from '@apollo/client';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import GalleryAd from '../Gallery/ad';
import GalleryItem from '../Gallery/item';
import CategoryFaq from './category-faq';
import CategoryPagination from './category-pagination';
import FilterAsideLazyLoad from './filter-aside-lazy-load';
import { useGaViewItemList } from '@/hooks/Category/use-ga-view-item-list';

const CategoryNoResult = dynamic(() => import('./category-no-result'), { ssr: false });
const CategoryDescription = dynamic(() => import('./category-description'), {
  ssr: false,
});
const FixedFilterBarLazyLoad = dynamic(() => import('./fixed-filter-bar-lazy-load'), {
  ssr: false,
});
const FilterModal = dynamic(() => import('@/components/filter-section/filter-modal'), {
  ssr: false,
});

const toSortedFilterOptions = prevOptions => {
  return prevOptions.slice(0).sort((a, b) => {
    const positionA = a.position;
    const positionB = b.position;

    if (positionA === positionB) return 0;

    if (positionA === null && positionB !== null) return 1;

    if (positionA !== null && positionB === null) return -1;

    return positionA - positionB;
  });
};

const CategoryContent = props => {
  const {
    name,
    description,
    categoryUrlKey,
    category_image,
    uid,
    currentPage,
    filterState,
    defaultSortState,
    aggregations,
    availableSortFields,
    layerFilterLabels,
    pageH1,
    titleH1,
    colorsFamilyOptions,
    hideColorsFamily,
    breadCrumb,
    categoryFilter,
    filterColor,
    pageSize,
    defaultCategoryState,
    breadCrumbInfo,
    useServerState: propsUseServerState,
    categoryAllFilterOptions,
    categoryIntId,
    filterFabric,
    categoryFaq,
  } = props;

  const sortedColorsFamilyOptions = Array.isArray(colorsFamilyOptions)
    ? [...colorsFamilyOptions].sort((a, b) => {
        if (a?.label === 'All') return -1;
        if (b?.label === 'All') return 1;
        if (a?.label === 'Floral') return 1;
        if (b?.label === 'Floral') return -1;
        return 0;
      })
    : colorsFamilyOptions;

  const isAttireCategory =
    ACTIVATE_ATTIRE_CATEGORIES.includes(breadCrumbInfo?.uid) ||
    (Array.isArray(breadCrumbInfo?.breadcrumbs) &&
      breadCrumbInfo.breadcrumbs.some(x => ACTIVATE_ATTIRE_CATEGORIES.includes(x?.category_uid)));
  const searchParams = useSearchParams();
  const querySortBy = searchParams.get('sort_by');
  const queryColor = searchParams.get('color');
  const queryColorsFamily = searchParams.get(COLORS_FAMILY_KEY);
  const useServerState = propsUseServerState && !querySortBy && !queryColor && !queryColorsFamily;
  const querySortState = allSortMethod.find(x => x.sortQuery === searchParams.get('sort_by'));
  const sortState = querySortState || defaultSortState;
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const router = useRouter();
  const [
    { headerOnTop, headerToolbarHeight, headerHeight },
    {
      actions: { setActiveColumn },
    },
  ] = useAppContext();

  const headerVisibleHeight = headerHeight - (headerOnTop ? 0 : headerToolbarHeight);

  const { ref: staticFilterBarRef, inView: staticFilterBarInView } = useInView({
    rootMargin: `-${headerHeight - headerVisibleHeight}px 0px 0px 0px`,
    threshold: 0,
    skip: !isMobile,
    initialInView: true,
  });

  const fixedFilterBarInView = !staticFilterBarInView;

  const [showFilterModal, setShowFilterModal] = useState(false);

  const { clientCategoryFilter, clientFilterState, clientCategoryFilterLabels, clientFilterColor, clientFilterFabric } =
    useMemo(() => {
      if (useServerState) {
        return {
          clientCategoryFilter: categoryFilter,
          clientFilterState: filterState,
          clientCategoryFilterLabels: layerFilterLabels,
          clientFilterColor: filterColor,
          clientFilterFabric: filterFabric,
        };
      }

      const { filterState: clientFilterState } = getFilterStateFromPath({
        categoryUrlKey,
        aggregations,
        pathname,
        filterOptions: categoryAllFilterOptions,
        queryColorsFamily,
        queryColor,
      });
      const colorsFamilyOptions =
        categoryAllFilterOptions.find(x => x.attribute_code === COLORS_FAMILY_KEY)?.options || [];
      const colorOptions =
        categoryAllFilterOptions.find(x => x.attribute_code === 'custom_option_color_ids')?.options || [];
      const { colorsFamilyRewrite, colorRewrite } = pathname.split('/').reduce(
        (acc, cur, i, arr) => {
          if (cur && i > 0 && arr[i - 1] === COLORS_FAMILY_KEY) {
            acc.colorsFamilyRewrite = cur;
          }

          if (cur && i > 0 && arr[i - 1] === 'color') {
            acc.colorRewrite = cur;
          }

          return acc;
        },
        {
          colorsFamilyRewrite: '',
          colorRewrite: '',
        },
      );
      const queryColorsFamilyRewrites = queryColorsFamily?.split(',') || [];
      const queryColorRewrites = queryColor?.split(',') || [];
      const categoryFilterLabels = resolveCategoryLayerFilterLabels({
        colorsFamilyRewrite,
        colorRewrite,
        colorOptions,
        aggregations,
        colorsFamilyOptions,
        filterState: clientFilterState,
        queryColorsFamilyRewrites,
        queryColorRewrites,
      });

      return {
        clientCategoryFilter: {
          category_id: {
            eq: categoryIntId,
          },
          ...convertStateToNewFilter(clientFilterState, hideColorsFamily),
        },
        clientFilterState: clientFilterState,
        clientCategoryFilterLabels: categoryFilterLabels,
        clientFilterColor: (clientFilterState.find(x => x.attributeCode === 'custom_option_color_ids')?.options || [])
          .map(x => x.label)
          .reverse()
          .join(','),
        clientFilterFabric:
          (clientFilterState.find(x => x.attributeCode === 'aw_general_fabric')?.options || [])[0]?.label || '',
      };
    }, [
      queryColor,
      queryColorsFamily,
      categoryUrlKey,
      aggregations,
      pathname,
      categoryAllFilterOptions,
      categoryIntId,
      hideColorsFamily,
      categoryFilter,
      filterColor,
      filterFabric,
      layerFilterLabels,
      filterState,
      useServerState,
    ]);

  const [internalFilterState, setInternalFilterState] = useState(useServerState ? filterState : clientFilterState);
  const querySku = searchParams.get('sku');
  const queryFilterColor = searchParams.get('fcolor');
  const prevPropsRef = useRef({ filterState, clientFilterState });

  useEffect(() => {
    if (searchParams.get('sort_by')) {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [searchParams]);

  const skuList = querySku
    ? querySku.split(',').reduce((acc, cur) => {
        if (cur && cur.trim()) {
          acc.push(cur.toUpperCase());
        }

        return acc;
      }, [])
    : [];
  const { data: categoryQuerySkuRes, loading: categoryQuerySkuLoading } = useQuery(gql(GET_CATEGORY_QUERY_SKU), {
    variables: {
      filters: {
        sku: {
          in: skuList,
        },
      },
      filter_color: queryFilterColor,
    },
    skip:
      !Array.isArray(skuList) ||
      skuList.length === 0 ||
      !(
        websiteEnv.isUnitedStates ||
        websiteEnv.isUnitedKingdom ||
        websiteEnv.isCanada ||
        websiteEnv.isAustralia ||
        websiteEnv.isGermanyEnglish
      ),
  });
  const categoryQuerySkuProducts =
    categoryQuerySkuRes?.products?.items?.slice(0)?.sort((a, b) => {
      return skuList.indexOf(a?.sku) - skuList.indexOf(b?.sku);
    }) || [];
  const categoryQuerySkuProductsCount = Array.isArray(categoryQuerySkuProducts) ? categoryQuerySkuProducts.length : 0;

  if (prevPropsRef.current.filterState !== filterState) {
    prevPropsRef.current.filterState = filterState;
    setInternalFilterState(filterState);
  }

  if (prevPropsRef.current.clientFilterState !== clientFilterState) {
    prevPropsRef.current.clientFilterState = clientFilterState;
    setInternalFilterState(clientFilterState);
  }

  useEffect(() => {
    setActiveColumn(isAttireCategory ? 'attire' : 'home');
  }, [isAttireCategory, setActiveColumn]);

  const isSearchParamNotMatch = window.location.search.slice(1) !== searchParams.toString();

  const { data: filteredCategoryOptionsRes, loading: filteredCategoryOptionsResLoading } = useQuery(
    gql(internalFilterState.length === 0 ? GET_CATEGORY_FILTER_OPTIONS : GET_CATEGORY_FILTER_OPTIONS_BY_FILTER),
    {
      variables: {
        filter: clientCategoryFilter,
      },
      skip: useServerState || isSearchParamNotMatch,
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: categoryProductsRes, loading: categoryProductsResLoading } = useQuery(
    gql(internalFilterState.length === 0 ? GET_CATEGORY_PRODUCTS : GET_CATEGORY_PRODUCTS_BY_FILTER),
    {
      variables: {
        currentPage: currentPage,
        id: uid,
        filters: clientCategoryFilter,
        pageSize,
        sort: sortState
          ? {
              [sortState.sortAttribute]: sortState.sortDirection,
            }
          : {},
        filter_color: clientFilterColor,
        filter_fabric: clientFilterFabric,
      },
      skip: useServerState || isSearchParamNotMatch,
      fetchPolicy: 'cache-and-network',
      onCompleted(data) {
        const responseTotalPage = data?.products?.page_info?.total_pages || 0;
        const items = data?.products?.items || [];
        const nextPathname = pathname
          .split('/')
          .reduce((acc, cur, i, arr) => {
            if (cur === 'p' || (i > 0 && arr[i - 1] === 'p' && /^\d+$/.test(cur))) {
              return acc;
            }

            acc.push(cur);

            return acc;
          }, [])
          .join('/');

        if (items.length === 0 && responseTotalPage < currentPage && pathname !== nextPathname) {
          router.replace(nextPathname + window.location.search);
        }
      },
    },
  );

  const { categoryAdvs, categoryProducts, totalPages, totalCount } = (() => {
    if (useServerState) {
      return defaultCategoryState;
    }

    const categoryAdvs = categoryProductsRes?.categories?.items?.[0]?.adv || [];
    const categoryProductsInfo = categoryProductsRes?.products;
    const categoryProducts = categoryProductsInfo?.items || [];
    const totalPages = categoryProductsInfo?.page_info?.total_pages || 0;
    const totalCount = categoryProductsInfo?.total_count || 0;

    return {
      categoryAdvs,
      categoryProducts,
      totalPages,
      totalCount,
    };
  })();

  useGaViewItemList({
    categoryProducts,
    categoryName: name,
    categoryUid: uid,
    categoryLoading: categoryProductsResLoading || categoryQuerySkuLoading,
  });

  const filterOptions = useMemo(() => {
    if (useServerState) {
      return toSortedFilterOptions(defaultCategoryState.filterOptions);
    }

    const filteredCategoryFilterOptions = (filteredCategoryOptionsRes?.products?.aggregations || []).filter(
      x => x.attribute_code !== 'custom_option_fabric_ids_bucket',
    );

    return toSortedFilterOptions(filteredCategoryFilterOptions);
  }, [filteredCategoryOptionsRes, useServerState, defaultCategoryState]);

  const filterBarColorOptions = filterOptions.find(x => x?.attribute_code === 'custom_option_color_ids')?.options || [];

  const filterLabelGroup =
    clientCategoryFilterLabels.length > 0
      ? clientCategoryFilterLabels.map(x => x.split(',').join(', ')).join(', ')
      : '';

  useEffect(() => {
    if (!globalThis.scrollTo) {
      return;
    }

    globalThis.scrollTo({
      behavior: 'smooth',
      left: 0,
      top: 0,
    });
  }, [currentPage]);

  const freeShipping = uid === 'MTYy' || uid === 'OQ==';

  const optionIdMapToRewrite = useMemo(() => {
    return aggregations.reduce((acc, cur) => {
      const { option_id, rewrite, code } = cur;

      if (!rewrite || !option_id || !code) {
        return acc;
      }

      if (acc[code]) {
        acc[code][option_id] = rewrite;
      } else {
        acc[code] = {
          [option_id]: rewrite,
        };
      }

      return acc;
    }, {});
  }, [aggregations]);

  const showColorsFamilyFilter =
    !hideColorsFamily &&
    Array.isArray(sortedColorsFamilyOptions) &&
    sortedColorsFamilyOptions.length &&
    Array.isArray(aggregations) &&
    aggregations.length;

  const content = (() => {
    if (categoryProductsResLoading || categoryQuerySkuLoading || categoryQuerySkuProductsCount > 0 || totalCount > 0) {
      const { advCount, advMap } = Array.isArray(categoryAdvs)
        ? categoryAdvs.reduce(
            (acc, cur) => {
              acc.advCount++;
              acc.advMap[cur.position - 1] = cur;

              return acc;
            },
            {
              advCount: 0,
              advMap: {},
            },
          )
        : {
            advCount: 0,
            advMap: {},
          };
      let productIndex = 0;

      return (
        <>
          <section className='grid grid-cols-2 gap-x-2 gap-y-[30px] lg_grid-cols-4 lg_gap-y-[54px]'>
            {categoryQuerySkuLoading
              ? skuList.map((x, i) => {
                  return (
                    <div
                      key={i}
                      className='relative'
                      style={{
                        paddingBottom: `${(470 / 320) * 100}%`,
                      }}
                    >
                      <img
                        src={NEXT_IMAGE_PLACEHOLDER}
                        className='absolute left-0 top-0 h-auto w-full'
                        alt=''
                      />
                    </div>
                  );
                })
              : categoryQuerySkuProducts.map(product => {
                  if (!product) return null;

                  const categories = Array.isArray(product.categories) ? product.categories : [];
                  const freeShipping = categories.some(x => {
                    return x?.uid === 'MTYy' || x?.uid === 'OQ==';
                  });
                  const isSeoCategory = categories.some(x => {
                    return SEO_CATEGORY_MAP[x?.uid];
                  });

                  return (
                    <GalleryItem
                      key={product.id}
                      item={product}
                      inCategoryPage
                      freeShipping={freeShipping}
                      isSeoCategory={isSeoCategory}
                    />
                  );
                })}
            {categoryProductsResLoading
              ? Array.from(
                  {
                    length: 36,
                  },
                  (_, i) => (
                    <div key={i}>
                      <Image
                        src={logoGif}
                        width={280}
                        height={410}
                        alt='placeholder'
                        className='h-auto w-full'
                      />
                    </div>
                  ),
                )
              : Array.from({ length: categoryProducts.length + advCount }).reduce((acc, cur, i) => {
                  const adv = advMap[i];

                  if (adv) {
                    acc.push(
                      <GalleryAd
                        key={adv.id}
                        item={adv}
                      />,
                    );
                    return acc;
                  }

                  const product = productIndex < categoryProducts.length ? categoryProducts[productIndex] : null;

                  if (product) {
                    acc.push(
                      <GalleryItem
                        key={product.id}
                        item={product}
                        freeShipping={freeShipping}
                        inCategoryPage
                        categoryName={name}
                        categoryId={uid}
                        showSpecial12Off
                        isSeoCategory={SEO_CATEGORY_MAP[uid]}
                      />,
                    );
                  }
                  productIndex++;

                  return acc;
                }, [])}
          </section>
          {totalPages > 1 ? (
            <Suspense>
              <div className='mt-[14px] lg_mt-[38px]'>
                <CategoryPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                />
              </div>
            </Suspense>
          ) : null}
        </>
      );
    }

    return (
      <CategoryNoResult
        url_key={categoryUrlKey}
        filterLabelGroup={filterLabelGroup}
      />
    );
  })();

  return (
    <>
      {isMobile && filterOptions.length ? (
        <FilterModal
          filterOptions={filterOptions}
          filterState={internalFilterState}
          curCategoryUrlKey={categoryUrlKey}
          categoryUid={uid}
          isSearchPage={false}
          hideColorsFamily={hideColorsFamily}
          open={showFilterModal}
          onSetOpen={nextOpen => {
            setShowFilterModal(nextOpen);
          }}
          colorsFamilyOptions={sortedColorsFamilyOptions}
          optionIdMapToRewrite={optionIdMapToRewrite}
          aggregations={aggregations}
        />
      ) : null}

      <div className='hidden py-[60px] lg_block'>
        <h1 className='px-2.5 text-center font-Shango text-[32px] leading-[38px]'>{pageH1 || name}</h1>
        <p className='mt-5 text-center text-[17px] leading-6'>{titleH1}</p>
      </div>

      {breadCrumb}

      <article className='mx-auto  mt-[31px] max-w-[1600px] pb-[45px]  lg_mt-10'>
        {category_image ? (
          <div className='mb-[56px] hidden  w-full px-2.5 lg_block lg_p-0'>
            <img
              src={process.env.NEXT_PUBLIC_BASE_PATH + category_image}
              loading='eager'
              alt={name}
              className='h-auto w-full'
            />
          </div>
        ) : null}
        <div className='flex  flex-col gap-x-4 gap-y-2.5 px-2.5 lg_hidden lg_px-0'>
          <h1 className='font-Shango  text-xl leading-6 '>{pageH1 || name}</h1>
          <div className='flex-none text-sm leading-[18px] lg_text-8B857D'>
            {totalCount} Result{totalCount > 1 ? 's' : ''}
          </div>
        </div>
        {showColorsFamilyFilter ? (
          <div className='mx-auto mt-[30px] max-w-site lg_mt-0'>
            <h2 className='hidden text-center text-base font-medium leading-[22px] lg_block'>
              {uid === 'NA=='
                ? '2026/27 Bridesmaid Colors'
                : websiteEnv.isUnitedKingdom
                  ? 'SHOP BY COLOUR'
                  : 'SHOP BY COLOR'}
            </h2>
            <ColorsFamilyFilter
              colorsFamilyOptions={sortedColorsFamilyOptions}
              aggregations={aggregations}
              optionIdMapToRewrite={optionIdMapToRewrite}
              categoryUrlKey={categoryUrlKey}
              className='lg_mt-[25px] '
              filterState={internalFilterState}
            />
          </div>
        ) : null}
        <div className='mt-[37px] w-full px-2.5 lg_flex lg_gap-x-[29px] lg_px-0'>
          {!useServerState && filteredCategoryOptionsResLoading && !filteredCategoryOptionsRes ? (
            <div className='hidden w-[269px] flex-none lg_block'></div>
          ) : (
            <FilterAsideLazyLoad
              filterOptions={filterOptions}
              filterState={internalFilterState}
              onSetFilterState={nextFilterState => {
                setInternalFilterState(nextFilterState);
              }}
              curCategoryUrlKey={categoryUrlKey}
              categoryUid={uid}
              isSearchPage={false}
              hideColorsFamily={hideColorsFamily}
              optionIdMapToRewrite={optionIdMapToRewrite}
              headerVisibleHeight={headerVisibleHeight}
            />
          )}
          <div className='lg_flex-1'>
            <div
              ref={staticFilterBarRef}
              className='flex items-center justify-between'
            >
              <div className='hidden flex-none text-sm leading-[18px] lg_block lg_text-8B857D'>
                {totalCount} Result{totalCount > 1 ? 's' : ''}
              </div>
              <div
                className={clsx(' grid flex-1 items-center lg_flex lg_flex-none lg_gap-x-2', ' grid-cols-2 gap-x-2')}
              >
                {filterOptions.length > 0 ? (
                  <button
                    className={clsx(
                      'flex flex-1 items-center justify-center bg-f8f3ef lg_hidden ',
                      'gap-x-2.5 py-[13px]',
                    )}
                    type='button'
                    aria-live='polite'
                    aria-busy='false'
                    onClick={() => {
                      setShowFilterModal(true);
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='17'
                      height='12'
                      fill='none'
                      viewBox='0 0 17 12'
                    >
                      <g clipPath='url(#clip0_10578_75403)'>
                        <path
                          fill='#80583F'
                          d='M7.558 1.716a2.14 2.14 0 0 0-.75-1.232 2.174 2.174 0 0 0-2.737 0 2.14 2.14 0 0 0-.75 1.232H.25v.858h3.07c.1.484.365.92.75 1.232a2.174 2.174 0 0 0 2.738 0 2.14 2.14 0 0 0 .75-1.232h8.692v-.858zM5.44 3.431a1.3 1.3 0 0 1-.746-.239 1.3 1.3 0 0 1-.551-1.047q.005-.22.078-.429c.087-.252.25-.47.47-.626a1.297 1.297 0 0 1 2.046 1.055q-.005.22-.077.429c-.09.249-.256.465-.474.618a1.3 1.3 0 0 1-.746.239'
                        />
                        <path
                          fill='#000'
                          fillOpacity='0.2'
                          d='M7.558 1.716a2.14 2.14 0 0 0-.75-1.232 2.174 2.174 0 0 0-2.737 0 2.14 2.14 0 0 0-.75 1.232H.25v.858h3.07c.1.484.365.92.75 1.232a2.174 2.174 0 0 0 2.738 0 2.14 2.14 0 0 0 .75-1.232h8.692v-.858zM5.44 3.431a1.3 1.3 0 0 1-.746-.239 1.3 1.3 0 0 1-.551-1.047q.005-.22.078-.429c.087-.252.25-.47.47-.626a1.297 1.297 0 0 1 2.046 1.055q-.005.22-.077.429c-.09.249-.256.465-.474.618a1.3 1.3 0 0 1-.746.239'
                        />
                        <path
                          fill='#80583F'
                          d='M11.06 7.711c-.498 0-.98.17-1.367.483a2.14 2.14 0 0 0-.751 1.232H.25v.857h8.692c.073.51.33.978.723 1.316a2.174 2.174 0 0 0 2.834 0c.394-.338.65-.805.724-1.316h3.027v-.857h-3.027a2.14 2.14 0 0 0-.768-1.245 2.17 2.17 0 0 0-1.394-.47m0 3.429a1.3 1.3 0 0 1-.745-.239 1.3 1.3 0 0 1-.474-.618 1.22 1.22 0 0 1 0-.857c.087-.252.252-.471.47-.626a1.297 1.297 0 0 1 1.97.626c.103.276.103.58 0 .857a1.313 1.313 0 0 1-1.22.857'
                        />
                        <path
                          fill='#000'
                          fillOpacity='0.2'
                          d='M11.06 7.711c-.498 0-.98.17-1.367.483a2.14 2.14 0 0 0-.751 1.232H.25v.857h8.692c.073.51.33.978.723 1.316a2.174 2.174 0 0 0 2.834 0c.394-.338.65-.805.724-1.316h3.027v-.857h-3.027a2.14 2.14 0 0 0-.768-1.245 2.17 2.17 0 0 0-1.394-.47m0 3.429a1.3 1.3 0 0 1-.745-.239 1.3 1.3 0 0 1-.474-.618 1.22 1.22 0 0 1 0-.857c.087-.252.252-.471.47-.626a1.297 1.297 0 0 1 1.97.626c.103.276.103.58 0 .857a1.313 1.313 0 0 1-1.22.857'
                        />
                      </g>
                      <defs>
                        <clipPath id='clip0_10578_75403'>
                          <path
                            fill='#fff'
                            d='M.25 0h16v12h-16z'
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className={clsx('text-80583F lg_text-base lg_leading-6', 'text-[17px]  leading-6')}>
                      FILTER ({useServerState ? filterState.length : clientFilterState.length})
                    </span>
                  </button>
                ) : (
                  <div className='flex flex-1 items-center justify-center gap-x-2.5 py-[13px]  lg_hidden'></div>
                )}
                {availableSortFields.length > 0 && (
                  <Suspense fallback={<div className='h-[50px] lg_h-11'></div>}>
                    <ProductSort
                      sortState={sortState}
                      availableSortMethods={availableSortFields}
                      inFilterBar={false}
                    />
                  </Suspense>
                )}
              </div>
            </div>
            <div className='mt-1.5 lg_mt-[15px] '>{content}</div>
            {String(currentPage) === '1' && (
              <CategoryDescription
                description={description}
                filterLabelGroup={filterLabelGroup}
              />
            )}
            {Array.isArray(categoryFaq) && categoryFaq.length > 0 ? <CategoryFaq faq={categoryFaq} /> : null}
          </div>
        </div>
      </article>

      {fixedFilterBarInView ? (
        <FixedFilterBarLazyLoad
          staticFilterBarInView={staticFilterBarInView}
          setShowFilterModal={setShowFilterModal}
          filterStateCount={useServerState ? filterState.length : clientFilterState.length}
          sortState={sortState}
          availableSortFields={availableSortFields}
          colorsFamilyOptions={sortedColorsFamilyOptions}
          filterBarColorOptions={filterBarColorOptions}
          aggregations={aggregations}
          optionIdMapToRewrite={optionIdMapToRewrite}
          categoryUrlKey={categoryUrlKey}
          internalFilterState={internalFilterState}
          hideColorsFamily={hideColorsFamily}
        />
      ) : null}
    </>
  );
};

export default CategoryContent;
